import * as React from 'react';
import * as ReactDOM from 'react-dom';
import WebGLDrawInstruction from './WebGLDrawInstruction';
import WebGLText from './WebGLText';
import WebGLTextOrientation from './WebGLTextOrientation';
import Color from './Color';
import Shape from './shapes/Shape';

export default class WebGLDirector {

	private context: WebGL2RenderingContext;
	private program: WebGLProgram;

	private transformationMatrixLocation: WebGLUniformLocation;
	private positionAttributeLocation: number;
	private colorAttributeLocation: number;

	private vertices: number[] = [];
	private positionBuffer: WebGLBuffer;
	private colorBuffer: WebGLBuffer;

	private defaultColor = Color.white();
	private colors: number[] = [];

	private texts: WebGLText[] = [];
	private textElements: HTMLElement[] = [];
	private drawInstructions: WebGLDrawInstruction[] = [];

	public constructor (canvas: HTMLCanvasElement, private width: number, private height: number, private textOverlay: HTMLDivElement) {
		const context = canvas.getContext('webgl2');

		if (context === null || !(context instanceof WebGL2RenderingContext)) {
			const message = <span style={{color: '#aa3333'}}>
				We are sorry, but visualizations cannot be displayed in the web browsers Safari and Internet explorer.<br /><br />
				Please install <a target='_blank' href='https://www.google.com/chrome/browser/desktop/index.html'>Google Chrome</a> or <a target='_blank' href='https://www.mozilla.org/en-US/firefox/new/'>Mozilla Firefox</a> in order to see visualizations.
			</span>;

			const messageElement = document.createElement('div');
			ReactDOM.render(message, messageElement);
			this.textOverlay.appendChild(messageElement);
			throw new Error('WebGL 2 not supported.');
		}

		this.context = context;

		const vertexShader = this.compile(this.getVertexShaderSource(), WebGLRenderingContext.VERTEX_SHADER);
		const fragmentShader = this.compile(this.getFragmentShaderSource(), WebGLRenderingContext.FRAGMENT_SHADER);

		this.program = this.link(vertexShader, fragmentShader);

		context.clearColor(0, 0, 0, 0);

		// context.enable(context.CULL_FACE);
		context.depthFunc(context.LEQUAL);
		context.enable(context.DEPTH_TEST);

		const transformationMatrixLocation = context.getUniformLocation(this.program, 'transformationMatrix');
		if (transformationMatrixLocation === null) {
			throw new Error('Could not get transformation matrix location.');
		}

		this.transformationMatrixLocation = transformationMatrixLocation;
		this.positionAttributeLocation = context.getAttribLocation(this.program, 'vertexPosition');
		this.colorAttributeLocation = context.getAttribLocation(this.program, 'vertexColor');

		this.positionBuffer = this.createBuffer();
		this.colorBuffer = this.createBuffer();

		const vao = context.createVertexArray();
		context.bindVertexArray(vao);

		context.useProgram(this.program);
	}

	/*
	 * Command methods
	 */

	public addText (text: WebGLText) {
		this.texts.push(text);
	}

	public addShapes (shape: Shape[]) {
		shape.forEach(shape => this.addShape(shape));

		return this;
	}

	public addShape (shape: Shape) {
		shape.getDrawInfo(this.defaultColor).forEach(drawInfo => {
			this.vertices = this.vertices.concat(drawInfo.getVertexArray());
			this.colors = this.colors.concat(drawInfo.getColorArray());
			this.drawInstructions.push(new WebGLDrawInstruction(drawInfo.getMode(), drawInfo.getVertexArray().length / 4, drawInfo.getTransformationMatrix()));
		});

		return this;
	}

	public clearScene () {
		this.context.clear(this.context.COLOR_BUFFER_BIT | this.context.DEPTH_BUFFER_BIT);
		this.textElements.forEach(element => this.textOverlay.removeChild(element));
		this.textElements = [];
	}

	public drawScene () {
		const context = this.context;

		context.bindBuffer(context.ARRAY_BUFFER, this.positionBuffer);
		context.enableVertexAttribArray(this.positionAttributeLocation);
		context.vertexAttribPointer(this.positionAttributeLocation, 4, context.FLOAT, false, 0, 0);
		this.context.bufferData(context.ARRAY_BUFFER, new Float32Array(this.vertices), context.DYNAMIC_DRAW);

		context.bindBuffer(context.ARRAY_BUFFER, this.colorBuffer);
		context.enableVertexAttribArray(this.colorAttributeLocation);
		context.vertexAttribPointer(this.colorAttributeLocation, 4, context.FLOAT, false, 0, 0);
		this.context.bufferData(context.ARRAY_BUFFER, new Float32Array(this.colors), context.DYNAMIC_DRAW);

		this.clearScene();

		let first = 0;
		for (let i = 0; i < this.drawInstructions.length; i++) {
			const drawInstructions = this.drawInstructions[i];
			const count = drawInstructions.getCount();
			context.uniformMatrix4fv(this.transformationMatrixLocation, false, drawInstructions.getTransformationMatrix().transpose().getData());
			context.drawArrays(drawInstructions.getMode(), first, count);
			first += count;
		}

		for (let i = 0; i < this.texts.length; i++) {
			this.drawText(this.texts[i]);
		}
	}

	/*
	 * Private methods
	 */

	private createBuffer () {
		const buffer = this.context.createBuffer();

		if (buffer === null) {
			throw new Error(`Could not create buffer.`);
		}

		return buffer;
	}

	private drawText (text: WebGLText) {
		const halfWidth = this.width / 2;
		const halfHeight = this.height / 2;

		const x = text.getX() * halfWidth;
		const y = text.getY() * halfHeight;
		const orientation = text.getOrientation();
		const margin = text.getPixelMargin();

		const element = document.createElement('div');
		const content = text.getText();
		if (typeof content === 'string') {
			element.innerText = content;

		} else {
			ReactDOM.render(content, element);
		}

		element.style.position = 'absolute';

		if (orientation === WebGLTextOrientation.TOP_LEFT || orientation === WebGLTextOrientation.BOTTOM_LEFT) {
			element.style.right = halfWidth - x + margin + 'px';
		} else {
			element.style.left = halfWidth + x + margin + 'px';
		}

		if (orientation === WebGLTextOrientation.BOTTOM_LEFT || orientation === WebGLTextOrientation.BOTTOM_RIGHT) {
			element.style.top = halfHeight - y + margin + 'px';
		} else {
			element.style.bottom = halfHeight + y + margin + 'px';
		}

		this.textOverlay.appendChild(element);
		this.textElements.push(element);
	}

	private compile (source: string, type: number) {
		const context = this.context;

		const shader = context.createShader(type);
		context.shaderSource(shader, source);
		context.compileShader(shader);

		const compiledSuccessfully = context.getShaderParameter(shader, WebGLRenderingContext.COMPILE_STATUS);

		if (!compiledSuccessfully || shader === null) {
			throw Error('Shader compilation failed:' + context.getShaderInfoLog(shader));
		}

		return shader;
	}

	private link (vertexShader: WebGLShader, fragmentShader: WebGLShader) {
		const context = this.context;

		const program = context.createProgram();

		context.attachShader(program, vertexShader);
		context.attachShader(program, fragmentShader);

		context.linkProgram(program);

		const linkedSuccessfully = context.getProgramParameter(program, WebGLRenderingContext.LINK_STATUS);

		if (!linkedSuccessfully || program === null) {
			throw ('Program linking failed: ' + context.getProgramInfoLog(program));
		}

		return program;
	}

	private getFragmentShaderSource () {
		return `#version 300 es

precision mediump float;

in vec4 color;

out vec4 vertexColor;

void main() {
	vertexColor = color;
}
		`;
	}

	private getVertexShaderSource () {
		return `#version 300 es

uniform mat4 transformationMatrix;

in vec4 vertexPosition;
in vec4 vertexColor;

out vec4 color;

void main() {
	gl_Position = transformationMatrix * vertexPosition;
	color = vertexColor;
}
		`;
	}
}


// WEBPACK FOOTER //
// ./code/project/pages/components/webGL/WebGLDirector.tsx